import React, { useEffect } from 'react';
import UserSelectionPage from './components/UserSelectionPage';
import LoadingSpinner from './components/LoadingSpinner';
import ResultPage from './components/ResultPage';
import DiffPage from './components/DiffPage';
import useAppStore from './store/useAppStore';
import { 
  loadMockRecommendation, 
  runRecommendation,
  loadMockMemoryUpdate,
  runMemoryUpdate 
} from './services/api';
import './App.css';

/**
 * 主应用组件
 * 根据全局状态控制不同页面的显示
 */
function App() {
  const currentStep = useAppStore((state) => state.currentStep);
  const selectedUser = useAppStore((state) => state.selectedUser);
  const recommendationResult = useAppStore((state) => state.recommendationResult);
  const setRecommendationResult = useAppStore((state) => state.setRecommendationResult);
  const setMemoryDiff = useAppStore((state) => state.setMemoryDiff);
  const setError = useAppStore((state) => state.setError);

  // 监听步骤变化，自动调用对应的 API
  useEffect(() => {
    if (currentStep === 'processing_recommendation') {
      // 调用推荐 API
      fetchRecommendation();
    } else if (currentStep === 'processing_update') {
      // 调用记忆更新 API
      fetchMemoryUpdate();
    }
  }, [currentStep, selectedUser, recommendationResult]);

  /**
   * 调用推荐 API
   */
  const fetchRecommendation = async () => {
    if (!selectedUser) return;
    
    try {
      // 检查是否配置了真实 API
      const useRealAPI = import.meta.env.VITE_USE_REAL_API === 'true';
      
      let data;
      
      if (useRealAPI) {
        // Call real API
        console.log('Calling real recommendation API...');
        data = await runRecommendation(selectedUser.id);
      } else {
        // Use Mock data (load from case_study)
        console.log('Using recommendation mock data...');
        data = await loadMockRecommendation(selectedUser.id);
      }
      
      console.log('Recommendation result:', data);
      setRecommendationResult(data);
    } catch (error) {
      console.error('Error fetching recommendation:', error);
      setError(error.message || 'Recommendation request failed, please try again');
    }
  };

  /**
   * 调用记忆更新 API
   */
  const fetchMemoryUpdate = async () => {
    if (!selectedUser || !recommendationResult) return;
    
    try {
      // 检查是否配置了真实 API
      const useRealAPI = import.meta.env.VITE_USE_REAL_API === 'true';
      
      let data;
      
      if (useRealAPI) {
        // Call real API
        console.log('Calling real memory update API...');
        data = await runMemoryUpdate(selectedUser.id, recommendationResult.item_id);
      } else {
        // Use Mock data (load from case_study)
        console.log('Using memory update mock data...');
        data = await loadMockMemoryUpdate(selectedUser.id, recommendationResult.item_id);
      }
      
      console.log('Memory update result:', data);
      setMemoryDiff(data);
    } catch (error) {
      console.error('Error fetching memory update:', error);
      setError(error.message || 'Memory update request failed, please try again');
    }
  };

  /**
   * 渲染加载提示信息
   */
  const getLoadingMessage = () => {
    switch (currentStep) {
      case 'processing_recommendation':
        return 'Initializing MemRec Collaborative Retrieval...';
      case 'processing_update':
        return 'Performing Asynchronous Graph Propagation and Memory Update...';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="app">
      {/* 根据当前步骤显示不同内容 */}
      {currentStep === 'idle' && <UserSelectionPage />}
      
      {(currentStep === 'processing_recommendation' || 
        currentStep === 'processing_update') && (
        <LoadingSpinner message={getLoadingMessage()} />
      )}
      
      {currentStep === 'showing_result' && <ResultPage />}
      
      {currentStep === 'showing_diff' && <DiffPage />}
    </div>
  );
}

export default App;

